// src/pages/Orders.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMerchant } from '../context/MerchantContext';
import { listenToShopOrders, updateOrderStatus, merchantLogout, getMerchantShop, listenToForegroundMessages } from '../services/firebase';
import { attachReconnectingListener, requestLiveReconnect } from '../utils/liveListener';
import { orderSubtotal, paymentBadge } from '../utils/orders';
import { formatOrderTitle } from '../utils/orderRef';
import { deliveredOrderDayKey, todayKey, orderDayKey } from '../utils/dates';

const STATUS_FLOW = {
  pending: { next: 'confirmed', label: 'Accept & Start Preparing', color: 'var(--green)' },
  confirmed: { next: null, label: 'Waiting for rider pickup', color: 'var(--blue)' },
  ready: { next: null, label: 'Waiting for rider pickup', color: 'var(--blue)' },
  picked_up: { next: null, label: 'Out for Delivery', color: 'var(--orange)' },
  delivered: { next: null, label: 'Delivered ✓', color: 'var(--green)' },
  cancelled: { next: null, label: 'Cancelled', color: 'var(--red)' },
  failed: { next: null, label: 'Failed', color: 'var(--red)' },
};

const STATUS_LABELS = {
  pending: '⏳ Pending',
  confirmed: '✅ Confirmed — awaiting rider',
  ready: '✅ Confirmed — awaiting rider',
  picked_up: '🛵 Out for Delivery',
  delivered: '✓ Delivered',
  cancelled: '✕ Cancelled',
  failed: '✕ Failed',
};

export default function Orders() {
  const navigate = useNavigate();
  const { shop, merchant, setShop } = useMerchant();
  const [checkingShop, setCheckingShop] = useState(false);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('active');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [updating, setUpdating] = useState(null);
  const prevPendingRef = useRef(0);
  const ordersReadyRef = useRef(false);

  useEffect(() => {
    if (!shop) return;
    setLoading(true);
    setLoadError(null);
    ordersReadyRef.current = false;
    return attachReconnectingListener(() => listenToShopOrders(
      shop.id,
      (data) => {
        const pending = data.filter((o) => o.status === 'pending').length;
        if (ordersReadyRef.current && pending > prevPendingRef.current) {
          toast('🛒 New order received!', { duration: 5000, icon: '📦' });
        }
        ordersReadyRef.current = true;
        prevPendingRef.current = pending;
        setOrders(data);
        setLoading(false);
        setLoadError(null);
      },
      (err) => {
        setLoading(false);
        setLoadError(err?.message || 'Could not load orders');
        toast.error('Could not load orders — reconnecting…');
      },
    ));
  }, [shop]);

  useEffect(() => {
    let unsub = () => {};
    listenToForegroundMessages((payload) => {
      const title = payload.notification?.title || 'New order';
      const body = payload.notification?.body || '';
      toast(`${title}${body ? ` — ${body}` : ''}`, { duration: 6000 });
      requestLiveReconnect();
    }).then((u) => { unsub = u; });
    return () => unsub();
  }, []);

  const handleUpdateStatus = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success(newStatus === 'confirmed' ? 'Packing started! 📦' : 'Order updated!');
    } catch (e) {
      toast.error('Could not update order');
    } finally { setUpdating(null); }
  };

  const handleReject = async (orderId) => {
    if (!window.confirm('Reject this order? The customer will be notified.')) return;
    setUpdating(orderId);
    try {
      await updateOrderStatus(orderId, 'cancelled', { cancellationReason: 'merchant_rejected' });
      toast('Order rejected — customer notified');
    } catch (e) {
      toast.error('Could not reject order');
    } finally { setUpdating(null); }
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Cancel this order? The customer will be notified.')) return;
    setUpdating(orderId);
    try {
      await updateOrderStatus(orderId, 'cancelled', { cancellationReason: 'merchant_cancelled' });
      toast.success('Order cancelled — customer notified');
    } catch (e) {
      toast.error('Could not cancel order');
    } finally { setUpdating(null); }
  };

  const filtered = orders.filter(o => {
    if (filter === 'active') return ['pending', 'confirmed', 'ready', 'picked_up'].includes(o.status);
    if (filter === 'delivered') return o.status === 'delivered';
    if (filter === 'cancelled') return o.status === 'cancelled';
    return true;
  });

  const pendingCount = orders.filter(o => o.status === 'pending').length;
  const today = todayKey();
  const todayRevenue = orders
    .filter(o => o.status === 'delivered' && deliveredOrderDayKey(o) === today)
    .reduce((s, o) => s + orderSubtotal(o), 0);
  const todayDeliveredCount = orders.filter(o => o.status === 'delivered' && deliveredOrderDayKey(o) === today).length;

  const formatTime = (order) => {
    const date = order.createdAt?.toDate?.();
    if (!date) return '';
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  if (!shop) return (
    <div style={{ minHeight: '100vh', background: 'var(--black)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 24 }}>
      <div style={{ fontSize: 48 }}>🏪</div>
      <div style={{ fontSize: 18, fontWeight: 800 }}>No shop linked</div>
      <div style={{ fontSize: 13, color: 'var(--gray)', textAlign: 'center', maxWidth: 320, lineHeight: 1.6 }}>
        Your account is not linked to any shop yet. Share the ID below with the MyTownly admin so they can link your shop.
      </div>
      {merchant?.uid && (
        <div style={{ width: '100%', maxWidth: 320, padding: 14, background: 'var(--card)', borderRadius: 12, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray)', letterSpacing: 0.5, marginBottom: 6 }}>YOUR MERCHANT ID</div>
          <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, wordBreak: 'break-all', color: 'var(--green)', marginBottom: 10 }}>{merchant.uid}</div>
          <button
            className="btn btn-green"
            style={{ width: '100%', fontSize: 13 }}
            onClick={() => {
              navigator.clipboard.writeText(merchant.uid);
              toast.success('Merchant ID copied!');
            }}
          >
            Copy ID for Admin
          </button>
          <button
            className="btn btn-dark"
            style={{ width: '100%', fontSize: 13, marginTop: 8 }}
            disabled={checkingShop}
            onClick={async () => {
              setCheckingShop(true);
              const linked = await getMerchantShop(merchant.uid);
              setShop(linked);
              setCheckingShop(false);
              if (linked) toast.success('Shop linked! Welcome 🏪');
              else toast('Still not linked — ask admin to save your ID');
            }}
          >
            {checkingShop ? 'Checking…' : 'Check again'}
          </button>
        </div>
      )}
      <button className="btn btn-dark" style={{ maxWidth: 200 }} onClick={() => merchantLogout()}>Logout</button>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--black)', maxWidth: 900, margin: '0 auto' }}>

      {/* Topbar */}
      <div style={{ background: 'var(--dark)', borderBottom: '1px solid var(--border)', padding: '0 24px', display: 'flex', alignItems: 'center', height: 60, gap: 16 }}>
        <div style={{ fontSize: 20 }}>🏪</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--green)' }}>{shop.name}</div>
          <div style={{ fontSize: 11, color: 'var(--gray)' }}>Merchant Dashboard</div>
        </div>
        {pendingCount > 0 && (
          <div style={{ background: 'var(--orange)', color: '#0a0a0a', fontSize: 12, fontWeight: 800, padding: '4px 10px', borderRadius: 20 }}>
            {pendingCount} new
          </div>
        )}
        {/* Nav */}
        {[
          { label: '📦 Orders', path: '/', active: true },
          { label: '💰 Earnings', path: '/earnings' },
          { label: '🛒 Products', path: '/products' },
          { label: '⚙️ Settings', path: '/settings' },
        ].map(n => (
          <button key={n.label} onClick={() => navigate(n.path)} style={{
            padding: '7px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: n.active ? 'rgba(0,214,143,0.1)' : 'transparent',
            color: n.active ? 'var(--green)' : 'var(--gray)',
            fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 700,
          }}>{n.label}</button>
        ))}
        <button onClick={() => merchantLogout()} style={{ background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: 13 }}>Logout</button>
      </div>

      <div style={{ padding: 24 }}>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: "Today's Revenue", value: `₹${todayRevenue}`, icon: '💰', color: 'var(--green)', hint: 'delivered subtotal' },
            { label: "Today's Orders", value: orders.filter(o => orderDayKey(o) === today).length, icon: '📦', color: 'var(--blue)' },
            { label: 'Delivered Today', value: todayDeliveredCount, icon: '✅', color: 'var(--green)' },
            { label: 'Pending', value: orders.filter(o => o.status === 'pending').length, icon: '⏳', color: 'var(--orange)' },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            { id: 'active', label: 'Active' },
            { id: 'delivered', label: 'Delivered' },
            { id: 'cancelled', label: 'Cancelled' },
            { id: 'all', label: 'All' },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} style={{
              padding: '8px 18px', borderRadius: 20,
              border: `1.5px solid ${filter === f.id ? 'var(--green)' : 'var(--border)'}`,
              background: filter === f.id ? 'rgba(0,214,143,0.1)' : 'var(--card)',
              color: filter === f.id ? 'var(--green)' : 'var(--gray)',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif',
            }}>{f.label}</button>
          ))}
        </div>

        {/* Orders list */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ width: 32, height: 32 }} /></div>
        ) : loadError ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--gray)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--orange)' }}>Could not load orders</div>
            <div style={{ fontSize: 12, marginTop: 8, lineHeight: 1.6 }}>{loadError}</div>
            <button className="btn btn-green" style={{ marginTop: 16, maxWidth: 200 }} onClick={() => window.location.reload()}>
              Refresh
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--gray)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>No orders here</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filtered.map(order => {
              const flow = STATUS_FLOW[order.status] || {};
              const isExpanded = expandedOrder === order.id;
              const subtotal = orderSubtotal(order);
              const deliveryFee = order.deliveryFee ?? Math.max(0, (order.total || 0) - subtotal);
              const pay = paymentBadge(order);
              return (
                <div key={order.id} className="card" style={{ overflow: 'hidden', border: order.status === 'pending' ? '1px solid rgba(245,158,11,0.4)' : '1px solid var(--border)' }}>
                  {/* Order header */}
                  <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                    onClick={() => setExpandedOrder(isExpanded ? null : order.id)}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 800 }}>{formatOrderTitle(order)}</span>
                        <span className={`badge badge-${order.status}`}>{STATUS_LABELS[order.status]}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'var(--dark)', color: 'var(--gray)' }}>
                          {pay.icon} {pay.label}
                        </span>
                        {order.status === 'pending' && <span style={{ fontSize: 10, color: 'var(--orange)', fontWeight: 700, animation: 'pulse 1s infinite' }}>● NEW</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--gray)' }}>
                        {order.items?.length || 0} items • ₹{subtotal} subtotal • {formatTime(order)}
                      </div>
                    </div>
                    <div style={{ fontSize: 18, color: 'var(--gray)', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none' }}>⌄</div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '14px 16px', animation: 'slideUp 0.2s ease' }}>
                      {/* Items */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 8 }}>ORDER ITEMS</div>
                        {order.items?.map((item, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                            <span>{item.name} × {item.qty}</span>
                            <span style={{ fontWeight: 700 }}>₹{item.price * item.qty}</span>
                          </div>
                        ))}
                        <div style={{ paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                            <span>Subtotal</span>
                            <span style={{ fontWeight: 700 }}>₹{subtotal}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, color: 'var(--gray)' }}>
                            <span>MyTownly delivery</span>
                            <span>₹{deliveryFee}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', fontSize: 14, fontWeight: 800 }}>
                            <span>Total (customer paid)</span>
                            <span style={{ color: 'var(--green)' }}>₹{order.total ?? subtotal + deliveryFee}</span>
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 4 }}>
                            Your earnings: ₹{subtotal} (subtotal only)
                          </div>
                        </div>
                      </div>

                      {order.deliveryPartnerId && (
                        <div style={{ marginBottom: 14, padding: 12, background: 'var(--dark)', borderRadius: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 4 }}>RIDER</div>
                          <div style={{ fontSize: 13 }}>🛵 {order.deliveryPartnerName || 'Assigned rider'}</div>
                          {order.deliveryPartnerPhone && (
                            <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>📞 {order.deliveryPartnerPhone}</div>
                          )}
                        </div>
                      )}

                      {/* Delivery address */}
                      <div style={{ marginBottom: 14, padding: 12, background: 'var(--dark)', borderRadius: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 4 }}>DELIVER TO</div>
                        <div style={{ fontSize: 13 }}>📍 {order.deliveryAddress || 'Not specified'}</div>
                        {order.customerPhone && <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>📞 {order.customerPhone}</div>}
                      </div>

                      {/* Action buttons */}
                      {flow.next && (
                        <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
                          <div style={{ display: 'flex', gap: 10 }}>
                            {order.status === 'pending' && (
                              <button className="btn btn-red" style={{ flex: 1 }} onClick={() => handleReject(order.id)} disabled={updating === order.id}>
                                ✕ Reject
                              </button>
                            )}
                            <button className="btn btn-green" style={{ flex: order.status === 'pending' ? 2 : 1 }} onClick={() => handleUpdateStatus(order.id, flow.next)} disabled={updating === order.id}>
                              {updating === order.id ? <span className="spinner" /> : flow.label}
                            </button>
                          </div>
                          {order.status === 'confirmed' && (
                            <button className="btn btn-red" style={{ width: '100%' }} onClick={() => handleCancel(order.id)} disabled={updating === order.id}>
                              ✕ Cancel order (out of stock / shop closed)
                            </button>
                          )}
                        </div>
                      )}
                      {!flow.next && order.status !== 'cancelled' && (
                        <div style={{ padding: '10px 14px', background: 'rgba(0,214,143,0.08)', borderRadius: 10, textAlign: 'center', fontSize: 13, color: 'var(--green)', fontWeight: 700 }}>
                          {flow.label}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
